/**
 * React Query hooks for the Third-party Services screen.
 *
 * One hook per vendor — each polls the matching /admin-services/:id
 * endpoint every 60 seconds, matching the legacy dashboard cadence
 * (src/views/admin/Services/index.jsx useServiceStatus interval).
 *
 * A 404 response is treated as data=null (not configured) rather than
 * an error, so react-query never enters an error state for
 * unconfigured integrations — the tile renders "not connected" instead.
 */
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  fetchServiceStatus,
  VendorData,
  VendorId
} from '@/api/adminServices';

const POLL_MS = 60_000;

function useServiceStatus(id: VendorId): UseQueryResult<VendorData | null> {
  return useQuery({
    queryKey: ['adminService', id],
    queryFn: () => fetchServiceStatus(id),
    refetchInterval: POLL_MS,
    // A null result (404/not-configured) is valid data, not an error.
    // react-query v5 handles this correctly since fetchServiceStatus
    // returns null rather than throwing on 404.
    retry: false
  });
}

export function useBrevoStatus() {
  return useServiceStatus('brevo');
}

export function useTwilioStatus() {
  return useServiceStatus('twilio');
}

export function useSentryStatus() {
  return useServiceStatus('sentry');
}

export function useBetterStackStatus() {
  return useServiceStatus('better-stack');
}

export function usePostHogStatus() {
  return useServiceStatus('posthog');
}

export function useCloudflareStatus() {
  return useServiceStatus('cloudflare');
}

/** Convenience: resolve the correct hook for the selected vendor id. */
export function useVendorStatus(id: VendorId): UseQueryResult<VendorData | null> {
  return useServiceStatus(id);
}
